/**
 * Event 配置块 JSON Schema 定义
 */
export const eventSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  $id: 'https://o2world.ai/schemas/config-types/event.json',
  title: 'Event 配置',
  description: 'Event（事件）配置数据结构',
  type: 'object',
  required: ['event_id', 'name', 'event_type'],
  properties: {
    event_id: {
      type: 'string',
      title: 'Event ID',
      description: 'Event 唯一标识符',
      pattern: '^event_[a-zA-Z0-9_]+$',
      minLength: 7,
      maxLength: 50,
      examples: ['event_boss_battle', 'item_discovery', 'plot_twist'],
    },
    name: {
      type: 'string',
      title: '事件名称',
      description: '事件的显示名称',
      minLength: 1,
      maxLength: 100,
      examples: ['BOSS 战', '发现物品', '剧情反转'],
    },
    description: {
      type: 'string',
      title: '事件描述',
      description: '事件的详细描述',
      minLength: 0,
      maxLength: 1000,
    },
    event_type: {
      type: 'string',
      title: '事件类型',
      enum: ['战斗', '对话', '探索', '选择', '环境变化', '剧情', '其他'],
    },
    trigger_conditions: {
      type: 'object',
      title: '触发条件',
      description: '事件触发的条件',
      properties: {
        location: {
          type: 'string',
          title: '地点',
          description: '需要在哪个地点触发',
          pattern: '^location_[a-zA-Z0-9_]+$',
        },
        time_range: {
          type: 'object',
          title: '时间范围',
          description: '触发的时间范围',
          properties: {
            start_time: {
              type: 'integer',
              title: '开始时间',
              minimum: 0,
            },
            end_time: {
              type: 'integer',
              title: '结束时间',
              minimum: 0,
            },
          },
        },
        variables: {
          type: 'object',
          title: '变量条件',
          description: '需要满足的变量条件',
          additionalProperties: true,
        },
        required_items: {
          type: 'array',
          title: '所需物品',
          items: {
            type: 'string',
            pattern: '^item_[a-zA-Z0-9_]+$',
          },
        },
        probability: {
          type: 'number',
          title: '触发概率',
          description: '每次尝试触发事件的概率（0-1）',
          minimum: 0,
          maximum: 1,
        },
      },
    },
    one_time_only: {
      type: 'boolean',
      title: '仅一次',
      description: '事件是否只能触发一次',
      default: false,
    },
    priority: {
      type: 'integer',
      title: '优先级',
      description: '同时触发时的优先级',
      minimum: 0,
      maximum: 100,
      default: 50,
    },
    consequences: {
      type: 'array',
      title: '后果',
      description: '事件触发后的后果',
      items: {
        type: 'object',
        required: ['consequence_type'],
        properties: {
          consequence_type: {
            type: 'string',
            enum: ['变量修改', '物品获取', '物品失去', 'NPC 状态变化', '地点变化', '触发新事件', '其他'],
          },
          target: {
            type: 'string',
            title: '目标',
            description: '后果作用的对象',
          },
          value: {
            title: '值',
            oneOf: [
              { type: 'string' },
              { type: 'number' },
              { type: 'boolean' },
              { type: 'object' },
            ],
          },
          description: {
            type: 'string',
            title: '后果描述',
            maxLength: 200,
          },
        },
      },
    },
    choices: {
      type: 'array',
      title: '选项',
      description: '事件触发的玩家选择',
      items: {
        type: 'object',
        required: ['choice_id', 'text'],
        properties: {
          choice_id: {
            type: 'string',
            title: '选项 ID',
          },
          text: {
            type: 'string',
            title: '选项文本',
            maxLength: 200,
          },
          consequences: {
            type: 'array',
            title: '后果',
            description: '选择此选项后的后果',
            items: {
              type: 'object',
              properties: {
                consequence_type: {
                  type: 'string',
                },
                target: {
                  type: 'string',
                },
                value: {
                  oneOf: [
                    { type: 'string' },
                    { type: 'number' },
                    { type: 'boolean' },
                  ],
                },
              },
            },
          },
        },
      },
    },
    auto_trigger: {
      type: 'boolean',
      title: '自动触发',
      description: '是否自动触发（无需玩家操作）',
      default: false,
    },
    repeatable: {
      type: 'boolean',
      title: '可重复',
      description: '是否可以重复触发',
      default: false,
    },
    cooldown: {
      type: 'integer',
      title: '冷却时间',
      description: '重复触发的冷却时间（秒）',
      minimum: 0,
    },
    metadata: {
      type: 'object',
      title: '元数据',
      description: '扩展元数据',
      additionalProperties: true,
    },
  },
  additionalProperties: false,
} as const

export type EventConfig = {
  event_id: string
  name: string
  description?: string
  event_type: string
  trigger_conditions?: {
    location?: string
    time_range?: {
      start_time?: number
      end_time?: number
    }
    variables?: Record<string, any>
    required_items?: string[]
    probability?: number
  }
  one_time_only?: boolean
  priority?: number
  consequences?: Array<{
    consequence_type: string
    target?: string
    value?: string | number | boolean | object
    description?: string
  }>
  choices?: Array<{
    choice_id: string
    text: string
    consequences?: Array<{
      consequence_type?: string
      target?: string
      value?: string | number | boolean
    }>
  }>
  auto_trigger?: boolean
  repeatable?: boolean
  cooldown?: number
  metadata?: Record<string, any>
}
